import { useState } from "react";
import {
  activityTypes,
  editorInlineItemsBehavior,
  resourceObjectTypes
} from "../constants";
import { buildPolicyPlayerResourceViewerRoute } from "../routes/LearningCenterRoutes";
import { useContentRepositoryAttachables } from "./contentRepositoryAttachables";
import { useCourses } from "./courseBuilder";
import { useContentRepositoryItems } from "./data/contentRepository";
import { useRepositoryItems } from "./repositoryItems";

export const usePolicyEditor = (editor, { contentRepositoryItemId }) => {
  const [isCreatingTrainingLinks, setIsCreatingTrainingLinks] = useState(false);
  const [
    isCreatingRepositoryItemLinks,
    setIsCreatingRepositoryItemLinks
  ] = useState(false);

  const { getCourses } = useCourses();
  const { fetchContentRepositoryItems } = useContentRepositoryItems();
  const { createItem } = useRepositoryItems();
  const {
    batchCreate: batchCreateContentRepositoryAttachables
  } = useContentRepositoryAttachables();

  const insertTrainingLink = (attachable, training) => {
    const trainingLink = {
      ...training,
      flags: training.flags.join(","),
      contentRepositoryAttachableId: attachable.id
    };
    editor.fire("addTrainingLink", trainingLink);
  };

  const createTrainingAttachables = (attachables, trainings) => {
    return batchCreateContentRepositoryAttachables(attachables).then(
      contentRepositoryAttachables =>
        contentRepositoryAttachables.forEach(attachable => {
          const training = trainings.find(
            ({ trainingObjectId }) => trainingObjectId == attachable.attached.id
          );
          insertTrainingLink(attachable, training);
        })
    );
  };

  /**
   * Inserts training links into the CKEditor document.
   *
   * @param {string[]} ctoaIds A list of CTOA ids that determines which training objects are used for the training links
   */
  const createTrainingLinks = async ctoaIds => {
    setIsCreatingTrainingLinks(true);
    await getCourses({
      page: { number: 0 },
      filters: { id: ctoaIds, parentTrainingObjectIdNull: true }
    })
      .then(({ data: trainings }) => {
        const attachables = trainings.map(training => ({
          contentRepositoryItem: {
            type: resourceObjectTypes.contentRepositoryItem,
            id: contentRepositoryItemId
          },
          attached: {
            type: resourceObjectTypes.trainingObject,
            id: training.trainingObjectId
          }
        }));
        return createTrainingAttachables(attachables, trainings);
      })
      .catch(error => {
        setIsCreatingTrainingLinks(false);
        throw error;
      });
    setIsCreatingTrainingLinks(false);
  };

  const embedMedia = (
    fileType,
    url,
    repositoryItemId,
    contentRepositoryAttachableId
  ) => {
    if (fileType === activityTypes.video) {
      editor.fire("addVideo", {
        url,
        repositoryItemId,
        contentRepositoryAttachableId
      });
    } else if (fileType === activityTypes.image) {
      editor.fire("addImage", {
        url,
        repositoryItemId,
        contentRepositoryAttachableId
      });
    }
  };

  const insertRepositoryItemLink = (attachable, repositoryItem) => {
    const repositoryItemData = {
      id: repositoryItem.id,
      name: repositoryItem.name,
      type: repositoryItem.prettyContentType,
      path: buildPolicyPlayerResourceViewerRoute(repositoryItem.id),
      contentRepositoryAttachableId: attachable.id
    };
    editor.fire("addRepositoryItemLinks", repositoryItemData);
  };

  const createRepositoryItemAttachables = (
    attachables,
    repositoryItems,
    editorBehavior
  ) => {
    return batchCreateContentRepositoryAttachables(attachables).then(
      contentRepositoryAttachables =>
        contentRepositoryAttachables.forEach(attachable => {
          const repositoryItem = repositoryItems.find(
            ({ id }) => id == attachable.attached.id
          );
          if (editorBehavior === editorInlineItemsBehavior.embed) {
            embedMedia(
              repositoryItem.prettyContentType,
              repositoryItem.previewUrl,
              repositoryItem.id,
              attachable.id
            );
          } else {
            insertRepositoryItemLink(attachable, repositoryItem);
          }
        })
    );
  };

  /**
   * Inserts repository item links into the CKEditor document.
   *
   * @param {string[]} repositoryItemIds A list of repository item ids that determines which items are used for the repository item links
   */
  const createRepositoryItemLinks = async repositoryItemIds => {
    setIsCreatingRepositoryItemLinks(true);
    await fetchContentRepositoryItems({
      page: { number: 0 },
      filters: { id: repositoryItemIds },
      fields: { repositoryItem: ["name", "prettyContentType"] }
    })
      .then(({ data: repositoryItems }) => {
        const attachables = repositoryItems.map(repositoryItem => ({
          contentRepositoryItem: {
            type: resourceObjectTypes.contentRepositoryItem,
            id: contentRepositoryItemId
          },
          attached: {
            type: resourceObjectTypes.contentRepositoryItem,
            id: repositoryItem.id
          }
        }));
        return createRepositoryItemAttachables(attachables, repositoryItems);
      })
      .catch(error => {
        setIsCreatingRepositoryItemLinks(false);
        throw error;
      });
    setIsCreatingRepositoryItemLinks(false);
  };

  const onAddRepositoryItem = newRepositoryItem => {
    return createItem({
      newItem: newRepositoryItem,
      skipScormImport: true
    }).then(repositoryItem => {
      const attachable = {
        contentRepositoryItem: {
          type: resourceObjectTypes.contentRepositoryItem,
          id: contentRepositoryItemId
        },
        attached: {
          type: resourceObjectTypes.contentRepositoryItem,
          id: repositoryItem.id
        }
      };
      return createRepositoryItemAttachables(
        [attachable],
        [repositoryItem],
        newRepositoryItem.editorBehavior
      );
    });
  };

  return {
    createRepositoryItemLinks,
    createTrainingLinks,
    onAddRepositoryItem,
    isCreatingRepositoryItemLinks,
    isCreatingTrainingLinks
  };
};
